import { Component, computed, DestroyRef, inject, Input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { ProductApiService } from '../../../../../services/productApi/product-api.service';
import { ProductListItem } from '../../../../../models/product-list/productListItem';
import { TunnelDisplayedProduct } from '../models/tunnel-displayed-product';
import { TunnelCartService } from '../services/tunnel-cart.service';

/** Recherche produit de l'étape « Liste d'achat », commune aux configurateurs. */
@Component({
  selector: 'ng-casto-tunnel-product-search',
  templateUrl: './tunnel-product-search.component.html',
  styleUrls: ['./tunnel-product-search.component.scss'],
})
export class TunnelProductSearchComponent {
  /** Clé de traduction du titre de la carte, à adapter selon l'écran qui héberge la recherche. */
  @Input() titleKey: string = 'tunnel.purchase-list.title';

  private readonly tunnelCartService = inject(TunnelCartService);
  private readonly productApi = inject(ProductApiService);
  private readonly destroyRef = inject(DestroyRef);

  /** Nombre de produits affichés par page de résultats. */
  protected readonly pageSize = 10;

  protected readonly searchValue = signal<string>('');
  protected readonly searchLoading = signal<boolean>(false);
  protected readonly totalResults = signal<number>(0);
  protected readonly firstResultIndex = signal<number>(0);

  /** Résultats de la recherche en cours, `null` lorsqu'aucune recherche n'est saisie. */
  private readonly searchResults = signal<ProductListItem[] | null>(null);

  private readonly searchInput$ = new Subject<string>();

  /** Indique qu'une recherche produit est en cours de consultation. */
  protected readonly isSearching = computed(() => this.searchResults() !== null);

  /** Résultats de recherche, avec la quantité déjà retenue dans la liste d'achat. */
  protected readonly searchProducts = computed<TunnelDisplayedProduct[]>(() =>
    (this.searchResults() ?? []).map(product => ({
      product,
      quantity: this.tunnelCartService.quantityOf(product.ean),
    }))
  );

  /** Clé de traduction affichée quand la recherche ne donne rien à afficher. */
  protected readonly emptyMessageKey = computed(() =>
    this.isSearching() ? 'tunnel.purchase-list.no-result' : 'tunnel.purchase-list.search-hint'
  );

  constructor() {
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => void this.search(value));
  }

  /** Enregistre la saisie de recherche, dont le traitement est différé. */
  protected onSearchInput(value: string): void {
    this.searchValue.set(value);
    this.searchInput$.next(value);
  }

  /** Charge la page de résultats demandée par le paginateur. */
  protected onPageChange(event: PaginatorState): void {
    this.firstResultIndex.set(event.first);
    void this.loadSearchPage(event.page + 1);
  }

  /** Lance une recherche produit, ou vide les résultats si la saisie est vide. */
  private async search(value: string): Promise<void> {
    if (!value?.trim()) {
      this.searchResults.set(null);
      this.totalResults.set(0);
      this.firstResultIndex.set(0);
      return;
    }

    this.firstResultIndex.set(0);
    await this.loadSearchPage(1);
  }

  private async loadSearchPage(page: number): Promise<void> {
    this.searchLoading.set(true);

    try {
      const response = await this.productApi.sendSearchRequest({
        searchTerm: this.searchValue().trim(),
        sortBy: null,
        page: page,
      });

      this.searchResults.set(response.data.map(item => new ProductListItem(item)));
      this.totalResults.set(response.meta?.paging?.totalResults ?? 0);
    } finally {
      this.searchLoading.set(false);
    }
  }
}
